/**
 * header-premium.js
 * Core controller: announcement bar slider, sticky/transparent header state,
 * and shared drawer open/close utilities used by search, cart, and mobile menu.
 */
(function () {
  'use strict';

  /* ---------------------------------------------------------------------
   * Shared drawer utility — exposed on window.PremiumHeader so the other
   * scripts (search, cart, mobile-menu) can reuse the same open/close logic.
   * ------------------------------------------------------------------- */
  var activeDrawer = null;
  var scrollLockCount = 0;

  function lockScroll() {
    scrollLockCount++;
    document.body.classList.add('premium-scroll-lock');
  }

  function unlockScroll() {
    scrollLockCount = Math.max(0, scrollLockCount - 1);
    if (scrollLockCount === 0) {
      document.body.classList.remove('premium-scroll-lock');
    }
  }

  function openDrawer(drawerEl, triggerEl) {
    if (!drawerEl) return;
    if (activeDrawer && activeDrawer !== drawerEl) {
      closeDrawer(activeDrawer);
    }
    drawerEl.hidden = false;
    // Force reflow so the transition runs
    void drawerEl.offsetHeight;
    drawerEl.classList.add('is-open');
    drawerEl.setAttribute('aria-hidden', 'false');
    if (triggerEl) triggerEl.setAttribute('aria-expanded', 'true');
    lockScroll();
    activeDrawer = drawerEl;

    var focusTarget = drawerEl.querySelector('[data-search-input], .cart-sidebar__close, .mobile-menu__close');
    if (focusTarget) {
      window.setTimeout(function () {
        focusTarget.focus();
      }, 350);
    }

    drawerEl.dispatchEvent(new CustomEvent('premium:drawer:open'));
  }

  function closeDrawer(drawerEl, triggerEl) {
    if (!drawerEl) return;
    drawerEl.classList.remove('is-open');
    drawerEl.setAttribute('aria-hidden', 'true');
    if (triggerEl) triggerEl.setAttribute('aria-expanded', 'false');
    unlockScroll();
    if (activeDrawer === drawerEl) activeDrawer = null;

    window.setTimeout(function () {
      if (!drawerEl.classList.contains('is-open')) {
        drawerEl.hidden = true;
      }
    }, 460);

    drawerEl.dispatchEvent(new CustomEvent('premium:drawer:close'));
  }

  window.PremiumHeader = {
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    lockScroll: lockScroll,
    unlockScroll: unlockScroll,
    getActiveDrawer: function () { return activeDrawer; }
  };

  // Global ESC key closes any open drawer
  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' && activeDrawer) {
      closeDrawer(activeDrawer);
    }
  });

  /* ---------------------------------------------------------------------
   * Announcement bar slider
   * ------------------------------------------------------------------- */
  function initAnnouncementBar(bar) {
    var slides = bar.querySelectorAll('.announcement-bar__slide');
    if (slides.length <= 1) return;

    var currentIndex = 0;
    var autoplay = bar.dataset.autoplay === 'true';
    var speed = parseInt(bar.dataset.autoplaySpeed, 10) || 5000;
    var pauseOnHover = bar.dataset.pauseHover === 'true';
    var loop = bar.dataset.loop === 'true';
    var timer = null;

    var prevBtn = bar.querySelector('.announcement-bar__arrow--prev');
    var nextBtn = bar.querySelector('.announcement-bar__arrow--next');

    function goTo(index) {
      slides[currentIndex].classList.remove('is-active');
      if (index >= slides.length) {
        index = loop ? 0 : slides.length - 1;
      }
      if (index < 0) {
        index = loop ? slides.length - 1 : 0;
      }
      currentIndex = index;
      slides[currentIndex].classList.add('is-active');
    }

    function next() { goTo(currentIndex + 1); }
    function prev() { goTo(currentIndex - 1); }

    function startAutoplay() {
      if (!autoplay) return;
      stopAutoplay();
      timer = window.setInterval(next, speed);
    }
    function stopAutoplay() {
      if (timer) {
        window.clearInterval(timer);
        timer = null;
      }
    }

    if (nextBtn) nextBtn.addEventListener('click', function () { next(); startAutoplay(); });
    if (prevBtn) prevBtn.addEventListener('click', function () { prev(); startAutoplay(); });

    if (pauseOnHover) {
      bar.addEventListener('mouseenter', stopAutoplay);
      bar.addEventListener('mouseleave', startAutoplay);
    }

    startAutoplay();
  }

  document.querySelectorAll('.announcement-bar').forEach(initAnnouncementBar);

  /* ---------------------------------------------------------------------
   * Sticky / transparent header scroll state
   * ------------------------------------------------------------------- */
  function initHeaderScrollState(header) {
    var isTransparent = header.dataset.transparent === 'true';
    if (!isTransparent) return;

    function update() {
      if (window.scrollY > 40) {
        header.classList.add('is-scrolled');
      } else {
        header.classList.remove('is-scrolled');
      }
    }

    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  document.querySelectorAll('.header-premium').forEach(initHeaderScrollState);

  /* ---------------------------------------------------------------------
   * Header overlay — closes the currently active drawer on click
   * ------------------------------------------------------------------- */
  document.querySelectorAll('[data-header-overlay]').forEach(function (overlay) {
    overlay.addEventListener('click', function () {
      if (activeDrawer) closeDrawer(activeDrawer);
    });
  });

})();
